<script setup>
import { computed } from 'vue';
import CountryFlag from 'vue-country-flag-next';

/**
 * A country's flag, sized and aligned like any other leading visual.
 *
 * Exists because the underlying package lays out a 52x39 sprite and then shrinks it with a
 * transform. A transform does not change the layout box, so the image ends up loose inside a box
 * of the original size, and the package compensates with margins that are even to the sides but
 * not top to bottom. A flag dropped straight into a row therefore sits low against its label.
 * This collapses the box onto the image so the flag occupies exactly what it draws, which is the
 * footprint a list row, a chip or a field expects any leading visual to reserve for itself.
 *
 * @status recommended
 * @group media
 * @useWhen Showing which country a value belongs to, beside its name
 * @avoidWhen Representing a person or an organisation — use HrAvatar
 * @avoidWhen A language rather than a country; the two do not map onto one another
 * @related HrAvatar
 * @a11y Decorative: the country name is expected beside it. Add aria-label when the flag stands alone.
 */
defineOptions({ name: 'HrCountryFlag' });

const props = defineProps({
  /**
   * ISO country code, in either the two or three letter form. Renders nothing when absent, so a
   * row whose value is not set yet does not need to guard the flag itself.
   * @example US
   */
  country: {
    type: String,
    default: null,
  },
  /**
   * Semantic size.
   * @values sm, md, lg
   */
  size: {
    type: String,
    default: 'md',
    validator: (v) => ['sm', 'md', 'lg'].includes(v),
  },
});

/** The package names its sizes differently, and offers only these three. */
const PACKAGE_SIZE = {
  sm: 'small',
  md: 'normal',
  lg: 'big',
};

const packageSize = computed(() => PACKAGE_SIZE[props.size]);
</script>

<template>
  <span
    v-if="props.country"
    :class="['hr-country-flag', `hr-country-flag--${props.size}`]"
  >
    <country-flag
      :country="props.country"
      :size="packageSize"
    />
  </span>
</template>

<style lang="scss" scoped>
/*
 * Inline-level, and aligned the way an icon is: dropped beside a label it should sit on the text
 * rather than on the baseline, where an image with no text in it would otherwise hang.
 */
.hr-country-flag {
  display: inline-flex;
  vertical-align: middle;
}

/*
 * The sprite is 52x39 and each size scales it, leaving that much box around a smaller image. Half
 * the difference on each side collapses the box onto what is drawn. In px because the sprite is:
 * the package expresses the same compensation in em, which is what makes it come out uneven.
 */
.hr-country-flag--sm :deep(.flag) {
  margin: -14.625px -19.5px;
}

.hr-country-flag--md :deep(.flag) {
  margin: -9.75px -13px;
}

.hr-country-flag--lg :deep(.flag) {
  margin: 0;
}
</style>
